import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Star, Loader2, RefreshCw, CheckCircle, Bot, UserCheck, ChevronDown, ChevronUp } from 'lucide-react';
import {
  generateEvaluationTargets,
  fetchEvaluationTargets,
  runAiEvaluation,
  confirmHumanScores,
  updateEvaluationMediaType,
} from '../api';

const MEDIA_TYPE_OPTIONS = ['일간지', '방송', '인터넷/IT', '경제', '전문지', '옥외', '기타'];

function MediaTypeEditor({ target, onChange }) {
  const current = MEDIA_TYPE_OPTIONS.includes(target.media_type) ? target.media_type : '';

  return (
    <select
      className="select"
      style={{ fontSize: '0.75rem', minWidth: '90px' }}
      value={current}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">선택</option>
      {MEDIA_TYPE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

const DIM_LABELS = {
  accessibility: '접근성',
  suitability: '적합성',
  activity: '활동성',
};
const DIM_MAX = { accessibility: 35, suitability: 35, activity: 30 };

function ScoreEditor({ target, onConfirm }) {
  const [values, setValues] = useState({
    accessibility: { level: target.ai_acc_score != null ? levelFromScore(target.ai_acc_score, 35) : 2, reason: target.human_acc_reason || target.ai_acc_reason || '' },
    suitability: { level: target.ai_suit_score != null ? levelFromScore(target.ai_suit_score, 35) : 2, reason: target.human_suit_reason || target.ai_suit_reason || '' },
    activity: { level: target.ai_act_score != null ? levelFromScore(target.ai_act_score, 30) : 2, reason: target.human_act_reason || target.ai_act_reason || '' },
  });
  const [changeReason, setChangeReason] = useState('');
  const [evaluator, setEvaluator] = useState('');

  function levelFromScore(score, max) {
    if (max === 30) return score === 30 ? 1 : score === 20 ? 2 : score === 10 ? 3 : 4;
    return score === max ? 1 : score === max - 10 ? 2 : score === max - 20 ? 3 : 4;
  }

  const total = Object.keys(DIM_MAX).reduce((sum, k) => {
    const level = parseInt(values[k].level);
    const score = k === 'activity' ? [30, 20, 10, 0][level - 1] : [35, 25, 15, 5][level - 1];
    return sum + score;
  }, 0);

  return (
    <div className="score-editor">
      <div className="score-editor-grid">
        {Object.keys(DIM_LABELS).map((key) => (
          <div className="score-editor-field" key={key}>
            <div className="score-editor-heading">
              <span>{DIM_LABELS[key]}</span>
              <span>{DIM_MAX[key]}점</span>
            </div>
            <div className="score-editor-label">평가 등급</div>
            <select
              className="select"
              style={{ width: '100%', fontSize: '0.8rem' }}
              value={values[key].level}
              onChange={(e) => setValues((p) => ({ ...p, [key]: { ...p[key], level: e.target.value } }))}
            >
              <option value={1}>1등급</option>
              <option value={2}>2등급</option>
              <option value={3}>3등급</option>
              <option value={4}>4등급</option>
            </select>
            <div className="score-editor-label">AI 판단 근거 <span>수정 가능</span></div>
            <textarea
              className="input"
              style={{ width: '100%', minHeight: '88px', fontSize: '0.75rem' }}
              placeholder="AI 판단 근거 또는 수정 내용"
              maxLength={200}
              value={values[key].reason}
              onChange={(e) => setValues((p) => ({ ...p, [key]: { ...p[key], reason: e.target.value } }))}
            />
            <div className="score-editor-count">{values[key].reason.length}/200자</div>
          </div>
        ))}
      </div>
      <div className="score-editor-meta">
        <label>
          <span>평가자</span>
          <input
            className="input"
            placeholder="평가자 이름"
            value={evaluator}
            onChange={(e) => setEvaluator(e.target.value)}
          />
        </label>
        <label>
          <span>변경 사유</span>
          <input
            className="input"
            placeholder="AI 평가와 다르게 판단한 사유"
            value={changeReason}
            onChange={(e) => setChangeReason(e.target.value)}
          />
        </label>
      </div>
      <div className="score-editor-footer">
        <div className="score-editor-total">총점: {total}점</div>
        <button
          className="btn btn-primary btn-sm"
          onClick={() => onConfirm({ values, changeReason, evaluator })}
        >
          <CheckCircle size={13} /> 사람 확정
        </button>
      </div>
    </div>
  );
}

export default function MediaEvaluationPanel() {
  const qc = useQueryClient();
  const [year, setYear] = useState(new Date().getFullYear());
  const [filter, setFilter] = useState('all');
  const [expanded, setExpanded] = useState({});

  const { data: targets, isLoading } = useQuery({
    queryKey: ['evaluation-targets', year, filter],
    queryFn: () => fetchEvaluationTargets({ year, inclusion: filter === 'all' ? undefined : filter }),
    enabled: true,
  });

  const genMut = useMutation({
    mutationFn: generateEvaluationTargets,
    onSuccess: () => qc.invalidateQueries(['evaluation-targets']),
  });

  const aiMut = useMutation({
    mutationFn: runAiEvaluation,
    onSuccess: () => qc.invalidateQueries(['evaluation-targets']),
  });

  const confirmMut = useMutation({
    mutationFn: ({ targetId, data }) => confirmHumanScores(targetId, data),
    onSuccess: () => qc.invalidateQueries(['evaluation-targets']),
  });

  const mediaTypeMut = useMutation({
    mutationFn: ({ targetId, mediaType }) => updateEvaluationMediaType(targetId, mediaType),
    onSuccess: () => qc.invalidateQueries(['evaluation-targets']),
  });

  const handleConfirm = (targetId, payload) => {
    const data = {
      accessibility: payload.values.accessibility,
      suitability: payload.values.suitability,
      activity: payload.values.activity,
      changeReason: payload.changeReason,
      evaluator: payload.evaluator,
    };
    confirmMut.mutate({ targetId, data });
  };

  const toggleExpand = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  const inclusionBadge = (type) => {
    const map = {
      BOTH: { label: '광고/보도자료 기사화', color: '#4ade80', bg: '#14532d' },
      AD_ONLY: { label: '광고만', color: '#fbbf24', bg: '#713f12' },
      PRESS_ONLY: { label: '보도자료 기사화만', color: '#38bdf8', bg: '#075985' },
      ARTICLE_ONLY: { label: '기사만', color: '#94a3b8', bg: '#1e293b' },
      MANUAL: { label: '수동', color: '#a78bfa', bg: '#4c1d95' },
    };
    const s = map[type] || { label: type, color: '#94a3b8', bg: '#1e293b' };
    return <span style={{ fontSize: '0.7rem', padding: '2px 6px', borderRadius: '4px', background: s.bg, color: s.color }}>{s.label}</span>;
  };

  const totalScore = (t) => {
    const s = [t.acc_score, t.suit_score, t.act_score].map(v => v || 0);
    return s.reduce((a, b) => a + b, 0);
  };

  const aiTotal = (t) => {
    const s = [t.ai_acc_score, t.ai_suit_score, t.ai_act_score].map(v => v || 0);
    return s.reduce((a, b) => a + b, 0);
  };

  return (
    <div>
      <div className="card" style={{ marginBottom: '1rem' }}>
        <h2 className="section-title"><Star size={15} /> 언론매체 평가 — 연도별 대상 생성</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <input
            type="number"
            className="input"
            style={{ width: '100px' }}
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value) || new Date().getFullYear())}
          />
          <button
            className="btn btn-primary btn-sm"
            onClick={() => genMut.mutate({ year })}
            disabled={genMut.isPending}
          >
            {genMut.isPending ? <Loader2 size={13} className="spin" /> : <RefreshCw size={13} />}
            평가대상 생성
          </button>
          <select className="select" style={{ fontSize: '0.85rem' }} value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="all">전체</option>
            <option value="BOTH">광고/보도자료 기사화</option>
            <option value="AD_ONLY">광고만</option>
            <option value="PRESS_ONLY">보도자료 기사화만</option>
          </select>
        </div>
        {genMut.isSuccess && (
          <div className="alert alert-success" style={{ marginTop: '0.75rem' }}>
            평가대상 {genMut.data.totalTargets}개 생성 / 교집합 {genMut.data.breakdown.both} / 광고만 {genMut.data.breakdown.adOnly} / 보도자료기사화만 {genMut.data.breakdown.pressOnly}
          </div>
        )}
      </div>

      <div className="card">
        <h2 className="section-title">평가대상 목록 {targets ? `(${targets.total || 0})` : ''}</h2>
        <p style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.75rem' }}>
          평가대상은 <b>광고 집행 매체</b>와 <b>보도자료 기사화 매체</b>의 합집합입니다. 기사 건수는 참고용입니다.
        </p>
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}><div className="spinner" /></div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="usage-table">
              <thead>
                <tr>
                  <th>매체명</th>
                  <th>매체종류</th>
                  <th>포함유형</th>
                  <th>보도자료</th>
                  <th>광고</th>
                  <th>AI 총점</th>
                  <th>확정 총점</th>
                  <th>상태</th>
                  <th>동작</th>
                </tr>
              </thead>
              <tbody>
                {(targets?.rows || []).map((t) => (
                  <React.Fragment key={t.id}>
                    <tr>
                      <td style={{ fontWeight: 600 }}>{t.standard_name}</td>
                      <td><MediaTypeEditor target={t} onChange={(val) => mediaTypeMut.mutate({ targetId: t.id, mediaType: val })} /></td>
                      <td>{inclusionBadge(t.inclusion_type)}</td>
                      <td>{t.press_article_count || '-'}</td>
                      <td>{t.ad_execution_amount ? `${(t.ad_execution_amount / 1000).toFixed(0)}백만` : '-'}</td>
                      <td>{aiTotal(t) || '-'}</td>
                      <td>{totalScore(t) || '-'}</td>
                      <td>
                        {t.target_status === 'confirmed' ? (
                          <span style={{ color: '#4ade80', fontSize: '0.75rem' }}>확정</span>
                        ) : (
                          <span style={{ color: '#fbbf24', fontSize: '0.75rem' }}>대기</span>
                        )}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '0.35rem' }}>
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => aiMut.mutate(t.id)}
                            disabled={aiMut.isPending}
                          >
                            <Bot size={12} /> AI 평가
                          </button>
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => toggleExpand(t.id)}
                          >
                            {expanded[t.id] ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                            점수
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expanded[t.id] && (
                      <tr>
                        <td colSpan={9} style={{ padding: 0, borderBottom: 'none' }}>
                          <ScoreEditor target={t} onConfirm={(payload) => handleConfirm(t.id, payload)} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
